import { useEffect, useRef, useState } from 'react'

import { notifyMethod } from '../../../../../App'
import useRedux from '../../../../../hooks/useRedux'
import useRouter from '../../../../../hooks/useRouter'
import useTranslations from '../../../../../hooks/useTranslations'
import ANTDButton from '../../../../../shared/antd/ANTDButton'
import { useFormFn, useWatchFn } from '../../../../../shared/antd/ANTDForm'
import { MAX_FILE_SIZE, userWiseRole } from '../../../../../utils/constant'
import { downloadReport } from '../../../../../utils/customFunctions'
import { dayJs, SAVE_FORMAT } from '../../../../../utils/dayjs'
import { excelIcon, excelLogo } from '../../../../../utils/icons'
import { include, isEqual, length } from '../../../../../utils/javascript'
import {
  bulkUploadApi,
  downloadTemplateApi,
  getBulkUploadListApi,
  getFileUploadDetailsApi,
  getSingleBulkUploadApi,
  uploadExcelApi,
} from '../../../jobs.api'
import { getJobTypeColumnTitles, tabKeys } from '../../../jobs.description'

const bulkUpload = ({ onFileUploadOrRemove }) => {
  const { t } = useTranslations()
  const form = useFormFn()
  const { location } = useRouter()
  const [viewModal, setViewModal] = useState({
    open: false,
    data: {},
    loader: false,
  })
  const [fileUploadDetailsModal, setFileUploadDetailsModal] = useState({
    open: false,
    data: {},
    loader: false,
    jobFileUploadId: null,
    jobType: null,
  })
  const [fileUploadColumns, setFileUploadColumns] = useState([])
  const [uploadLoader, setUploadLoader] = useState(false)
  const [downloadLoader, setDownloadLoader] = useState(false)
  const [bulkUploadList, setBulkUploadList] = useState({
    loader: false,
  })
  const [successPopupModal, setSuccessPopupModal] = useState(false)
  const bulkUploadPollingCleanupRef = useRef(null)
  const PAGE_SIZE = 5
  const BULK_UPLOAD_POLL_MS = 3000
  const BULK_UPLOAD_POLL_MAX_MS = 5 * 60 * 1000
  const uploadFile = useWatchFn('uploadFile', form)
  const { selector } = useRedux()
  const userData = selector(state => state.user?.profile_details)
  const { roleId } = { ...userData }
  const materialType = userData?.materialTypes?.[0]?.materialTypeKey

  const { recycler, procurement, procurementUser1, procurementUser2 } =
    userWiseRole

  useEffect(() => {
    getBulkUploadList()
    return () => {
      if (bulkUploadPollingCleanupRef.current) {
        bulkUploadPollingCleanupRef.current()
        bulkUploadPollingCleanupRef.current = null
      }
    }
  }, [])

  const getJobType = () => {
    switch (location?.state?.jobType) {
      case tabKeys.smeltingRequest:
        return 'RECYCLE'
      case tabKeys.smelting:
        return 'MATERIAL_PRODUCTION'
      case tabKeys.sorting:
        return 'SORTING'
      case tabKeys.grinding:
        return 'GRINDING'
      case tabKeys.postProcessing:
        return 'POST_PROCESSING'
      case tabKeys.compoundingPelletizing:
        return 'COMPOUNDING_PELLETIZING'
      case tabKeys.pelletizing:
        return 'PRE_COMPOUNDING_PELLETIZING'
      case tabKeys.materialSalesMaterialProducer:
        return 'MATERIAL_SALES_MATERIAL_PRODUCER'
      case tabKeys.materialSalesSupplier:
        return 'MATERIAL_SALES_PART_PRODUCER'
      case tabKeys.partSalesPartProducer:
        return 'PART_SALES_PART_PRODUCER'
      case tabKeys.supplierProduction:
        return 'PART_PRODUCTION'

      default:
        break
    }
  }

  const getSource = () => {
    switch (location?.state?.jobType) {
      case tabKeys.smeltingRequest:
        return isEqual(recycler, roleId) ? 'RECYCLE_CREATE' : 'RECYCLE_COMPLETE'
      case tabKeys.smelting:
        return 'MATERIAL_PRODUCTION_COMPLETE'
      case tabKeys.sorting:
        return 'SORTING_COMPLETE'
      case tabKeys.grinding:
        return 'GRINDING_COMPLETE'
      case tabKeys.postProcessing:
        return 'POST_PROCESSING_COMPLETE'
      case tabKeys.compoundingPelletizing:
        return 'COMPOUNDING_PELLETIZING_COMPLETE'
      case tabKeys.pelletizing:
        return 'PRE_COMPOUNDING_PELLETIZING_COMPLETE'
      case tabKeys.materialSalesMaterialProducer:
        return 'MATERIAL_SALES_MATERIAL_PRODUCER_CREATE'
      case tabKeys.materialSalesSupplier:
        return isEqual(materialType, 'job_Plastic')
          ? 'MATERIAL_SALES_PART_PRODUCER_PLASTIC_CREATE'
          : 'MATERIAL_SALES_PART_PRODUCER_CREATE'
      case tabKeys.partSalesPartProducer:
        return 'PART_SALES_PART_PRODUCER_CREATE'
      case tabKeys.supplierProduction:
        return 'PART_PRODUCTION_COMPLETE'

      default:
        break
    }
  }

  const titleFn = () => {
    switch (location?.state?.jobType) {
      case tabKeys.smeltingRequest:
        return t('job_DispatchRecycleRequestBulkUpload')
      case tabKeys.smelting: // Material production
        return t('job_MaterialProductionBulkUpload')
      case tabKeys.sorting:
        return t('job_SortingBulkUpload')
      case tabKeys.grinding:
        return t('job_GrindingBulkUpload')
      case tabKeys.postProcessing: // Post processing
        return t('job_PostProcessingBulkUpload')
      case tabKeys.compoundingPelletizing:
        return t('job_CompoundingPelletizingBulkUpload')
      case tabKeys.pelletizing:
        return t('job_PreCompoundingPelletizingBulkUpload')
      case tabKeys.materialSalesMaterialProducer:
        return t('job_MaterialSalesMaterialProducerBulkUpload')
      case tabKeys.materialSalesSupplier: // Material Sales (Part Producer) Job
        return t('job_MaterialSalesSupplierBulkUpload')
      case tabKeys.partSalesPartProducer:
        return t('job_PartSalesPartProducerBulkUpload')
      case tabKeys.supplierProduction: // Part Production Job
        return t('job_PartProductionBulkUpload')

      default:
        break
    }
  }

  const getSingleBulkUpload = async ({
    pageNo = 1,
    selectedRowId = viewModal?.selectedRow?.id,
  } = {}) => {
    setViewModal(prev => ({ ...prev, loader: true }))
    const payload = {
      jobFileUploadId: selectedRowId,
      pageSize: PAGE_SIZE,
      jobType: getJobType(),
      source: getSource(),
    }
    try {
      const res = await getSingleBulkUploadApi({ params: payload, pageNo })
      if (res?.data) {
        setViewModal(prev => ({ ...prev, data: res?.data }))
      }
    } catch (error) {
    } finally {
      setViewModal(prev => ({ ...prev, loader: false }))
    }
  }

  const getBulkUploadList = async ({ pageNo = 1, silent = false } = {}) => {
    const payload = {
      pageSize: PAGE_SIZE,
      jobType: getJobType(),
      source: getSource(),
    }
    if (!silent) {
      setBulkUploadList(prev => ({ ...prev, loader: true }))
    }
    try {
      const res = await getBulkUploadListApi({ params: payload, pageNo })
      if (res?.data) {
        setBulkUploadList({
          ...res?.data,
          loader: false,
        })
      }
      return res?.data || null
    } catch (error) {
      return null
    } finally {
      if (!silent) {
        setBulkUploadList(prev => ({ ...prev, loader: false }))
      }
    }
  }

  const stopBulkUploadPolling = () => {
    if (bulkUploadPollingCleanupRef.current) {
      bulkUploadPollingCleanupRef.current()
      bulkUploadPollingCleanupRef.current = null
    }
  }

  const startBulkUploadPollingAfterUpload = () => {
    stopBulkUploadPolling()
    let intervalId
    let maxTimerId
    const cleanup = () => {
      clearInterval(intervalId)
      clearTimeout(maxTimerId)
      if (bulkUploadPollingCleanupRef.current === cleanup) {
        bulkUploadPollingCleanupRef.current = null
      }
    }
    intervalId = setInterval(() => {
      getBulkUploadList({ silent: true })
    }, BULK_UPLOAD_POLL_MS)
    maxTimerId = setTimeout(cleanup, BULK_UPLOAD_POLL_MAX_MS)
    bulkUploadPollingCleanupRef.current = cleanup
  }

  const handleSuccessPopupModalClose = () => {
    setSuccessPopupModal(false)
  }

  const onFinish = async value => {
    if (length(value?.uploadFile?.fileList)) {
      const file = value?.uploadFile
      const isLt5MB =
        !(file?.file?.size || file?.file?.originFileObj?.size) ||
        Number(file?.file?.size || file?.file?.originFileObj?.size) <
          MAX_FILE_SIZE
      if (!isLt5MB) {
        notifyMethod.warning({
          message: t('msg_MaximumSizeAllowed', {
            maxSize: MAX_FILE_SIZE / 1024 / 1024,
          }),
        })
      }
      const dmsId = await onFileUploadOrRemove({ file })
      if (dmsId) {
        const materialTypeCond =
          materialType &&
          ((isEqual(tabKeys.materialSalesSupplier, location?.state?.jobType) &&
            isEqual(materialType, 'job_Plastic')) ||
            include(
              [
                tabKeys.supplierProduction,
                tabKeys.partSalesPartProducer,
                tabKeys.materialSalesMaterialProducer,
              ],
              location?.state?.jobType,
            ))
        const payload = {
          dmsId,
          jobType: getJobType(),
          source: getSource(),
          ...(materialTypeCond && {
            materialType: t(materialType)?.toUpperCase() || '',
          }),
        }
        setUploadLoader(true)
        try {
          let resp = null
          if (
            include(
              [
                tabKeys.smelting,
                tabKeys.sorting,
                tabKeys.grinding,
                tabKeys.postProcessing,
                tabKeys.compoundingPelletizing,
                tabKeys.pelletizing,
                tabKeys.materialSalesMaterialProducer,
                tabKeys.materialSalesSupplier,
                tabKeys.partSalesPartProducer,
                tabKeys.supplierProduction,
              ],
              location?.state?.jobType,
            ) ||
            (tabKeys.smeltingRequest === location?.state?.jobType &&
              include(
                [procurement, procurementUser1, procurementUser2],
                roleId,
              ))
          ) {
            resp = await uploadExcelApi({ params: payload })
          } else {
            resp = await bulkUploadApi({ payload })
          }
          getBulkUploadList()
          if (resp?.data) {
            startBulkUploadPollingAfterUpload()
            setSuccessPopupModal(true)
          }
          form.resetFields()
        } finally {
          setUploadLoader(false)
        }
      }
    }
  }

  const newColumns = [
    {
      title: t('translation:user_ID', 'ID'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('job_FileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 400,
      render: rowData => rowData || '-',
    },
    {
      title: t('job_UploadedDate'),
      dataIndex: 'creationDate',
      key: 'creationDate',
      render: rowData => (rowData ? dayJs(rowData).format(SAVE_FORMAT) : '-'),
    },
    {
      title: t('job_UploadedTime'),
      dataIndex: 'creationDate',
      key: 'creationDate',
      render: rowData => (rowData ? dayJs(rowData).format('HH:mm:ss') : '-'),
    },
    {
      title: t('job_DownloadFile'),
      key: 'fileName',
      render: rowData => {
        const report = rowData?.cdnUrl

        return (
          <img
            className={!report ? 'disabled-image' : 'cursor-pointer'}
            src={excelLogo}
            height={50}
            width={40}
            alt="excelImage"
            onClick={() => report && downloadReport(report)}
          />
        )
      },
    },
    {
      title: t('txt_Action'),
      key: 'action',
      align: 'center',
      render: rowData =>
        rowData && (
          <ANTDButton
            className="bg-view"
            onClick={() => handleFileUploadDetailsView(rowData)}
          >
            {t('btn_View')}
          </ANTDButton>
        ),
    },
  ]

  const columns = [
    {
      title: t('translation:user_ID', 'ID'),
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: t('job_FileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 400,
      render: rowData => rowData || '-',
    },
    {
      title: t('job_TotalRows'),
      dataIndex: 'totalRows',
      key: 'totalRows',
      render: rowData => (rowData >= 0 ? rowData : '-'),
    },
    {
      title: t('job_SuccessCount'),
      dataIndex: 'successCount',
      key: 'successCount',
      render: rowData => (rowData >= 0 ? rowData : '-'),
    },
    {
      title: t('job_Status'),
      dataIndex: 'status',
      key: 'status',
      render: rowData => (
        <p
          style={{
            color: rowData === 'PENDING' ? '#f4aa33' : '',
            fontWeight: 600,
          }}
        >
          {rowData || '-'}
        </p>
      ),
    },
    {
      title: t('job_UploadedAt'),
      dataIndex: 'creationDate',
      key: 'uploadDate',
      render: rowData => (rowData ? dayJs(rowData).format(SAVE_FORMAT) : '-'),
    },
    {
      title: t('Excel'),
      dataIndex: 'cdnUrl',
      key: 'Excel',
      render: (rowData, record) => {
        return (
          <img
            src={excelIcon}
            alt="excel"
            height={40}
            width={40}
            className="cursor-pointer"
            onClick={() => downloadReport(rowData, record?.fileName)}
          />
        )
      },
    },
    {
      title: t('txt_Action'),
      key: 'action',
      align: 'center',
      render: rowData =>
        rowData && (
          <div className="inline-flex">
            <ANTDButton
              className="bg-view"
              // onClick={() => {
              //   setViewModal({ open: true, selectedRow: rowData })
              //   getSingleBulkUpload({ selectedRowId: rowData?.id })
              // }}
              onClick={() => {
                if (include(['RECYCLE_CREATE'], getSource())) {
                  setViewModal({ open: true, selectedRow: rowData })
                  getSingleBulkUpload({ selectedRowId: rowData?.id })
                } else {
                  handleFileUploadDetailsView(rowData)
                }
              }}
            >
              View
            </ANTDButton>
          </div>
        ),
    },
  ]

  const viewColumns = [
    {
      title: t('job_Date'),
      dataIndex: 'columnDetails',
      key: 'job_Date',
      width: 120,
      render: rowData => {
        const data = JSON.parse(rowData)
        return data?.date ? dayJs(data?.date).format(SAVE_FORMAT) : '-'
      },
    },
    {
      title: t('dash_Particulars'),
      dataIndex: 'columnDetails',
      key: 'dash_Particulars',
      width: 350,
      render: rowData => {
        const data = JSON.parse(rowData)
        return (
          <div>
            <p>{data?.userBusinessName}</p>
            <p>{data?.bucketName}</p>
          </div>
        )
      },
    },
    {
      title: t('job_VchType'),
      dataIndex: 'columnDetails',
      key: 'job_VchType',
      width: 120,
      render: rowData => {
        const data = JSON.parse(rowData)
        return (
          <div>
            <p>{data?.vchType}</p>
            <p>{data?.quantityDispatched}</p>
          </div>
        )
      },
    },
    {
      title: t('job_VchNo'),
      dataIndex: 'columnDetails',
      key: 'job_VchNo',
      width: 180,
      render: rowData => {
        const data = JSON.parse(rowData)
        return (
          <div>
            <p>{data?.invoiceNumber}</p>
            <p>{data?.ratePerKg}</p>
          </div>
        )
      },
    },
    {
      title: t('job_Debit'),
      dataIndex: 'columnDetails',
      key: 'job_Debit',
      render: rowData => {
        const data = JSON.parse(rowData)
        return data?.totalAmount || '-'
      },
    },
    {
      title: t('job_Credit'),
      dataIndex: 'columnDetails',
      key: 'job_Credit',
      render: rowData => {
        const data = JSON.parse(rowData)
        return data?.credit || '-'
      },
    },
    {
      title: t('job_Status'),
      dataIndex: 'status',
      key: 'status',
      render: rowData => rowData || '-',
    },
    {
      title: t('job_Reason'),
      dataIndex: 'reason',
      key: 'job_Reason',
      render: rowData => rowData || '-',
      width: 300,
    },
  ]

  const handleChange = ({ current }) => {
    getBulkUploadList({ pageNo: current })
  }

  const handleSingleViewChange = ({ current }) => {
    getSingleBulkUpload({ pageNo: current })
  }

  const handleCloseModal = () => {
    setViewModal({ open: false, data: {}, loader: false })
  }

  const getFileUploadDetails = async ({
    pageNo = 1,
    jobFileUploadId = fileUploadDetailsModal?.jobFileUploadId,
    jobType = fileUploadDetailsModal?.jobType,
    newColumns = false,
  } = {}) => {
    if (!jobFileUploadId || !jobType) return

    setFileUploadDetailsModal(prev => ({ ...prev, loader: true }))
    const params = {
      jobFileUploadId,
      jobType,
      pageSize: 10,
    }
    try {
      const res = await getFileUploadDetailsApi({ params, pageNo })
      if (res?.data) {
        setFileUploadDetailsModal(prev => ({
          ...prev,
          data: res?.data,
        }))
        if (isEqual(pageNo, 1) && newColumns) {
          const columns = generateFileUploadDetailsColumns(
            jobType,
            res?.data?.list?.[0]?.columnDetails,
          )
          setFileUploadColumns(columns || [])
        }
      }
    } catch (error) {
    } finally {
      setFileUploadDetailsModal(prev => ({ ...prev, loader: false }))
    }
  }

  const handleFileUploadDetailsView = rowData => {
    const jobType = getJobType()
    setFileUploadDetailsModal({
      open: true,
      data: {},
      loader: false,
      jobFileUploadId: rowData?.id,
      jobType,
    })
    if (rowData?.id) {
      getFileUploadDetails({
        pageNo: 1,
        jobFileUploadId: rowData?.id,
        jobType,
        newColumns: true,
      })
    }
  }

  const handleFileUploadDetailsTableChange = ({ current }) => {
    getFileUploadDetails({
      pageNo: current,
      jobFileUploadId: fileUploadDetailsModal?.jobFileUploadId,
      jobType: fileUploadDetailsModal?.jobType,
    })
  }

  const handleFileUploadDetailsClose = () => {
    setFileUploadDetailsModal({
      open: false,
      data: {},
      loader: false,
      jobFileUploadId: null,
      jobType: null,
    })
  }

  const generateFileUploadDetailsColumns = (jobType, sampleColumnDetails) => {
    if (!jobType) return []
    try {
      // const parsedDetails = JSON.parse(sampleColumnDetails)
      const columnTitleKeys = getJobTypeColumnTitles(jobType, t, materialType)
      // const keys = Object.keys(parsedDetails).map(key => parseInt(key))

      const tableColumns = columnTitleKeys.map((key, index) => {
        // const translationKey = columnTitleKeys[index]
        return {
          title: key ? t(key) : `Column ${index + 1}`,
          dataIndex: key || `column_${index}`,
          key: key || `column_${index}`,
          render: value => {
            return value || '-'
          },
        }
      })
      const columns = [
        !include(columnTitleKeys, 'job_SNo')
          ? {
              title: t('job_SNo'),
              dataIndex: 'job_SNo',
              key: 'job_SNo',
              render: (_, __, index) => {
                return index + 1 || '-'
              },
            }
          : [],
        ...tableColumns,
        {
          title: t('job_Status'),
          dataIndex: 'job_Status',
          key: 'job_Status',
          render: value => {
            return value || '-'
          },
        },
        {
          title: t('job_Reason'),
          dataIndex: 'job_Reason',
          key: 'job_Reason',
          render: value => {
            return value || '-'
          },
        },
      ]

      return columns
    } catch (error) {
      return []
    }
  }

  // Transform data source to map columnDetails values
  const transformFileUploadDetailsData = (dataList, jobType) => {
    if (!dataList || !length(dataList)) return []

    const columnTitleKeys = getJobTypeColumnTitles(jobType, t, materialType)

    return dataList.map((item, index) => {
      const transformedItem = {
        key: item?.id || index,
        id: item?.id,
        job_Status: item?.status || '-',
        job_Reason: item?.reason || '-',
      }

      try {
        if (item?.columnDetails) {
          const parsedDetails = JSON.parse(item?.columnDetails || '{}')
          const keys = Object.keys(parsedDetails).map(key => parseInt(key))

          keys.forEach((key, idx) => {
            const translationKey = columnTitleKeys[idx]
            if (translationKey) {
              transformedItem[translationKey] = parsedDetails[key] || '-'
            }
          })
        }
      } catch (error) {}
      return transformedItem
    })
  }

  const getInstruction = () => {
    switch (location?.state?.jobType) {
      case tabKeys.smeltingRequest:
        return isEqual(recycler, roleId)
          ? [
              t('job_DispatchJobInstruction1'),
              t('job_DispatchJobInstruction2'),
              t('job_DispatchJobInstruction3'),
              t('job_DispatchJobInstruction4'),
              t('job_DispatchJobInstruction5'),
            ]
          : [
              t('job_ChecktheFormatoftheExcelSheet'),
              t('job_UploadtheFileFormat'),
              t(
                'job_ClickonUploadDispatchExcelSheetbuttonanduploadthesheetwithFilledDetails',
              ),
            ]
      // case tabKeys.smelting:
      //   return [
      //     t('job_ChecktheFormatoftheExcelSheet'),
      //     t('job_UploadtheFileFormat'),
      //     t('job_SmeltingJobInstruction3'),
      //   ]
      // case tabKeys.postProcessing:
      //   return [
      //     t('job_ChecktheFormatoftheExcelSheet'),
      //     t('job_UploadtheFileFormat'),
      //     t('job_PostProcessingJobInstruction3'),
      //   ]
      // case tabKeys.compoundingPelletizing:
      //   return [
      //     t('job_ChecktheFormatoftheExcelSheet'),
      //     t('job_UploadtheFileFormat'),
      //     t('job_CompoundingPelletizingBulkUploadInstruction3'),
      //   ]
      default:
        return [
          t('job_ChecktheFormatoftheExcelSheet'),
          t('job_UploadtheFileFormat'),
          t('job_bulkUploadInstruction'),
        ]
    }
  }

  const onDownloadTemplate = async () => {
    let fileTemplate = getSource()

    if (fileTemplate) {
      setDownloadLoader(true)
      try {
        const res = await downloadTemplateApi({
          params: { fileTemplate },
        })

        if (res?.data) {
          downloadReport(res?.data?.cdnUrl)
        } else if (res?.error?.errorMsg) {
          notifyMethod.error({
            message: res?.error?.errorMsg || t('msg_SomethingWentWrong'),
          })
        }
      } catch (error) {
      } finally {
        setDownloadLoader(false)
      }
    }
  }

  return {
    t,
    form,
    // columns,
    columns: include(
      [
        'RECYCLE_CREATE',
        'RECYCLE_COMPLETE',
        'MATERIAL_PRODUCTION_COMPLETE',
        'SORTING_COMPLETE',
        'GRINDING_COMPLETE',
        'POST_PROCESSING_COMPLETE',
        'COMPOUNDING_PELLETIZING_COMPLETE',
        'PRE_COMPOUNDING_PELLETIZING_COMPLETE',
        'MATERIAL_SALES_MATERIAL_PRODUCER_CREATE',
        'MATERIAL_SALES_PART_PRODUCER_CREATE',
        'MATERIAL_SALES_PART_PRODUCER_PLASTIC_CREATE',
        'PART_SALES_PART_PRODUCER_CREATE',
        'PART_PRODUCTION_COMPLETE',
      ],
      getSource(),
    )
      ? columns
      : newColumns,
    viewModal,
    fileUploadDetailsModal,
    PAGE_SIZE,
    viewColumns,
    bulkUploadList,
    titleFn,
    onFinish,
    handleChange,
    handleSingleViewChange,
    handleCloseModal,
    handleFileUploadDetailsView,
    handleFileUploadDetailsClose,
    handleFileUploadDetailsTableChange,
    transformFileUploadDetailsData,
    uploadLoader,
    downloadLoader,
    uploadFile,
    getInstruction,
    roleId,
    onDownloadTemplate,
    fileUploadColumns,
    successPopupModal,
    handleSuccessPopupModalClose,
  }
}

export default bulkUpload
